import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PublishIcon from "@mui/icons-material/Publish";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";

import SearchableTable from "./general/SearchableTable";
import MarketingUploadModal from "./MarketingUploadModal";
import MarketingImportModal from "./MarketingImportModal";
import RedirectsEditor from "./marketing/redirects/RedirectsEditor";
import RedirectsPublishSection from "./marketing/redirects/RedirectsPublishSection";
import { dateToSimpleString } from "./utils";

// What the user must type to confirm a risky production deploy. Phrased to name
// the actual danger (publishing untested content), not a generic "confirm", so
// that even a skim registers it. Content/marketing — not engineers — use this.
const CONFIRM_PHRASE = "publish without testing";

// Color for a deploy's last-known status in the deploy panel.
const STATUS_COLORS = {
  succeeded: "success.main",
  failed: "error.main",
  unknown: "warning.main",
};

const ARTICLE_TYPES = [
  "blog",
  "cheat-sheet",
  "guide",
  "tutorial",
  "newsletter",
];

// Base domain the article is served from, per environment.
const SITE_DOMAINS = {
  stage: "https://astro-stage.dataquest.io",
  production: "https://www.dataquest.io",
};

// Article type -> URL path segment on the site (note newsletter is pluralized).
const TYPE_PATHS = {
  blog: "blog",
  "cheat-sheet": "cheat-sheet",
  guide: "guide",
  tutorial: "tutorial",
  newsletter: "newsletters",
};

const articleUrl = (row) => {
  const domain = SITE_DOMAINS[row.environment];
  const path = TYPE_PATHS[row.type];
  if (!domain || !path) return null;
  return `${domain}/${path}/${row.slug}`;
};

const FILTERS = [
  {
    label: "Stage only",
    filter: (row) => row.environment === "stage",
  },
  {
    label: "Production only",
    filter: (row) => row.environment === "production",
  },
];

// Turn the ISO "T" separator into a space so the date/time can wrap onto
// two lines on narrow screens.
const formatDate = (value) => (value ? value.replace("T", " ") : "");

function MarketingList() {
  const history = useHistory();
  const { view } = useParams();
  const showingRedirects = view === "redirects";

  const [articles, setArticles] = useState([]);
  const [deployStatus, setDeployStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadModalIsOpen, setUploadModalIsOpen] = useState(false);
  const [importModalIsOpen, setImportModalIsOpen] = useState(false);
  const [publishModalIsOpen, setPublishModalIsOpen] = useState(false);
  const [redirectsRefreshKey, setRedirectsRefreshKey] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  // When set, the production deploy is awaiting the typed confirmation.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // Highest id per slug across the full history — only that (latest) row is
  // promotable, and only while it is still a stage entry.
  const latestIdBySlug = articles.reduce((acc, article) => {
    if (acc[article.slug] === undefined || article.id > acc[article.slug]) {
      acc[article.slug] = article.id;
    }
    return acc;
  }, {});

  const COLUMNS = [
    { key: "slug", label: "Slug", type: "text" },
    { key: "type", label: "Type", type: "text" },
    { key: "environment", label: "Environment", type: "text" },
    { key: "date", label: "Date", type: "text" },
    { key: "lastmod", label: "Last mod", type: "text" },
    { key: "r2_status", label: "R2", type: "text" },
    { key: "s3_status", label: "S3", type: "text" },
    { key: "user_name", label: "User", type: "text" },
    {
      key: "updated_at",
      label: "Updated",
      type: "component",
      render: (row) =>
        row.updated_at ? dateToSimpleString(new Date(row.updated_at)) : "",
    },
    {
      key: "site",
      label: "",
      type: "component",
      render: (row) => {
        const url = articleUrl(row);
        return url ? (
          <Link
            href={url}
            target="_blank"
            rel="noopener"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            Open
            <OpenInNewIcon fontSize="inherit" />
          </Link>
        ) : null;
      },
    },
    {
      key: "actions",
      label: "",
      type: "component",
      render: (row) =>
        row.environment === "stage" && row.id === latestIdBySlug[row.slug] ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => promoteArticle(row.id)}
          >
            Promote
          </Button>
        ) : null,
    },
    {
      key: "edit",
      label: "",
      type: "component",
      render: (row) =>
        row.environment === "stage" && row.id === latestIdBySlug[row.slug] ? (
          <IconButton
            size="small"
            onClick={() => history.push(`/marketing/article/${row.id}`)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];

  // `silent` refreshes without the loading spinner, used by the background poll.
  const refresh = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setError("");
      const [articlesResp, deployResp] = await Promise.all([
        fetch("/api/marketing/articles"),
        fetch("/api/marketing/deploy-status"),
      ]);
      const articlesData = await articlesResp.json();
      const deployData = await deployResp.json();
      if (articlesData.error) {
        setError(articlesData.error);
      } else {
        setArticles(articlesData);
      }
      if (!deployData.error) {
        setDeployStatus(deployData);
      }
      if (!silent) setIsLoading(false);
    } catch (e) {
      if (!silent) setIsLoading(false);
      setError("An error occurred while loading the marketing data.");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // While a deploy is in flight, poll so its status settles without a manual
  // refresh.
  const deployInProgress = Boolean(
    deployStatus?.stage?.in_progress || deployStatus?.prod?.in_progress
  );

  useEffect(() => {
    if (!deployInProgress) return undefined;
    const interval = setInterval(() => refresh(true), 20000);
    return () => clearInterval(interval);
  }, [deployInProgress, refresh]);

  const promoteArticle = async (articleId) => {
    try {
      setError("");
      const response = await fetch(
        `/api/marketing/articles/${articleId}/promote`,
        { method: "POST" }
      );
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        refresh();
      }
    } catch (e) {
      setError("An error occurred while promoting the article.");
    }
  };

  const deployEnvironment = async (environment, confirm = false) => {
    try {
      setError("");
      const response = await fetch(`/api/marketing/deploy/${environment}`, {
        method: "POST",
        headers: confirm ? { "Content-Type": "application/json" } : undefined,
        body: confirm ? JSON.stringify({ confirm: true }) : undefined,
      });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        refresh();
      }
    } catch (e) {
      setError("An error occurred while starting the deploy.");
    }
  };

  // Production deploys that would publish content never verified on stage go
  // through the typed-confirmation dialog; everything else deploys directly.
  const onDeployClick = (env) => {
    if (env.environment === "production" && env.needs_confirmation) {
      setConfirmText("");
      setConfirmOpen(true);
    } else {
      deployEnvironment(env.environment);
    }
  };

  const confirmProdDeploy = () => {
    setConfirmOpen(false);
    deployEnvironment("production", true);
  };

  const closeUploadHandler = () => {
    setUploadModalIsOpen(false);
    refresh();
  };

  const closeImportHandler = () => {
    setImportModalIsOpen(false);
    refresh();
  };

  // Memoized so SearchableTable gets a stable `rows` reference; a new array on
  // every render resets its filters/sort and forces an extra table render.
  const visibleArticles = useMemo(
    () =>
      (typeFilter === "all"
        ? articles
        : articles.filter((article) => article.type === typeFilter)
      ).map((article) => ({
        ...article,
        date: formatDate(article.date),
        lastmod: formatDate(article.lastmod),
        user_name: article.user_name || "",
      })),
    [articles, typeFilter]
  );

  const renderDeployRow = (label, env) => {
    if (!env) return null;
    const last = env.last_deploy;
    const statusColor = STATUS_COLORS[last?.status] || "text.primary";
    const statusEl = last?.url ? (
      <Link href={last.url} target="_blank" rel="noopener" color="inherit">
        {last.status}
      </Link>
    ) : (
      last?.status
    );
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ py: 0.5 }}
      >
        <Typography variant="body2" component="div">
          <strong>{label}</strong>
          {" — "}
          {last ? (
            <>
              last deploy{" "}
              <Box
                component="span"
                sx={{ color: statusColor, fontWeight: 600 }}
              >
                {statusEl}
              </Box>
              {last.triggered_at &&
                ` · ${dateToSimpleString(new Date(last.triggered_at))}`}
            </>
          ) : (
            "never deployed"
          )}
          {env.pending_count > 0 && ` · ${env.pending_count} pending`}
          {env.needs_confirmation && (
            <Box
              component="span"
              sx={{
                color: "error.main",
                ml: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                verticalAlign: "bottom",
              }}
            >
              <WarningAmberIcon fontSize="inherit" />
              {env.unverified_slugs?.length || 0} unverified on stage
            </Box>
          )}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color={env.needs_confirmation ? "error" : "primary"}
          disabled={!env.can_deploy}
          onClick={() => onDeployClick(env)}
        >
          {env.in_progress ? "Deploying…" : `Deploy ${label}`}
        </Button>
      </Stack>
    );
  };

  return (
    <Container sx={{ pb: 4 }}>
      <MarketingUploadModal
        open={uploadModalIsOpen}
        closeModal={() => setUploadModalIsOpen(false)}
        uploadedCallback={closeUploadHandler}
      />

      <MarketingImportModal
        open={importModalIsOpen}
        closeModal={() => setImportModalIsOpen(false)}
        importedCallback={closeImportHandler}
      />

      <Dialog
        open={publishModalIsOpen}
        onClose={() => setPublishModalIsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Publish
          <IconButton onClick={() => setPublishModalIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RedirectsPublishSection
            onPublished={() => setRedirectsRefreshKey((count) => count + 1)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ color: "error.main" }}>
          Publish unreviewed content to the live site?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            {deployStatus?.prod?.warning ? (
              <>
                <p>{deployStatus?.prod?.warning}</p>
                {deployStatus?.prod?.unverified_slugs.map((slug) => (
                  <p>{slug}</p>
                ))}
              </>
            ) : (
              "This production deploy includes content that was never deployed to stage."
            )}
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            Type <strong>{CONFIRM_PHRASE}</strong> to proceed.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            placeholder={CONFIRM_PHRASE}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={confirmText.trim().toLowerCase() !== CONFIRM_PHRASE}
            onClick={confirmProdDeploy}
          >
            Confirm and deploy
          </Button>
        </DialogActions>
      </Dialog>

      <Box my={5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4">Marketing</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {showingRedirects ? "Redirects" : "Articles"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              data-test-selector="toggle-redirects"
              onClick={() =>
                history.push(
                  showingRedirects ? "/marketing" : "/marketing/redirects"
                )
              }
            >
              {showingRedirects ? "Articles" : "Redirects"}
            </Button>
            {showingRedirects ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PublishIcon />}
                data-test-selector="open-publish"
                onClick={() => setPublishModalIsOpen(true)}
              >
                Publish
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  data-test-selector="new-import"
                  onClick={() => setImportModalIsOpen(true)}
                >
                  Import
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  data-test-selector="new-upload"
                  onClick={() => setUploadModalIsOpen(true)}
                >
                  New Upload
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      {showingRedirects ? (
        <RedirectsEditor refreshKey={redirectsRefreshKey} />
      ) : (
        <>
          {error && <Alert severity="error">{error}</Alert>}

          {deployStatus?.stage && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Deploy
              </Typography>
              {renderDeployRow("Stage", deployStatus.stage)}
              {renderDeployRow("Production", deployStatus.prod)}
            </Paper>
          )}

          <SearchableTable
            cols={COLUMNS}
            rows={visibleArticles}
            isLoading={isLoading}
            filters={FILTERS}
            rowsPerPage={10}
            filterControls={
              <FormControl
                size="small"
                sx={{
                  minWidth: 160,
                  "& .MuiInputBase-root": { height: 30.75 },
                }}
              >
                <InputLabel id="marketing-type-filter-label">Type</InputLabel>
                <Select
                  labelId="marketing-type-filter-label"
                  label="Type"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {ARTICLE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
        </>
      )}
    </Container>
  );
}

export default MarketingList;
